import type {
  ExpenseLineItem,
  ExpenseResponseDto,
  LineItemSplitResult,
  LineItemsRest,
} from '@splitfiesta/shared';

import {
  type ItemAvailability,
  availabilityOf,
  previewLineItemSplit,
  takeUnits,
  toggleUnit,
} from './lineItemsForm';

/** Units the viewer wants on a row; `base` is what they held on the loaded row when they changed it. */
export type PendingClaim = { want: number; base: number };
export type PendingClaims = Readonly<Record<string, PendingClaim>>;

const DEFAULT_REST: LineItemsRest = { mode: 'proportional' };

/** The viewer's units on a row as the claim sends them: a legacy sharer holds one. */
export function unitsHeld(
  item: ExpenseLineItem,
  viewerId: string,
  participants: readonly string[]
): number {
  const availability = availabilityOf(item, viewerId, participants);
  return availability.kind === 'mine' ? availability.mine : 0;
}

/**
 * Pending units after the loaded items changed. Pending units are a diff
 * against the loaded items, so a refetch that only moved other people's
 * picks keeps them. A change whose row the refetch changed for the viewer is
 * stale and dropped, so it cannot come back if those units later move back.
 * A change others outran is cut to what they left and its `base` kept, so a
 * unit freed later does not silently rejoin it.
 */
export function reconcilePendingClaims(
  prev: PendingClaims,
  items: readonly ExpenseLineItem[] | undefined,
  viewerId: string,
  participants: readonly string[]
): PendingClaims {
  const byId = new Map((items ?? []).map(item => [item.id, item]));
  const next: Record<string, PendingClaim> = {};
  for (const [id, change] of Object.entries(prev)) {
    const item = byId.get(id);
    if (!item || unitsHeld(item, viewerId, participants) !== change.base)
      continue;
    const [fitted] = takeUnits([item], id, viewerId, change.want, participants);
    const want = unitsHeld(fitted, viewerId, participants);
    if (want !== change.base) next[id] = { want, base: change.base };
  }
  return next;
}

/**
 * The rows with the viewer's pending units applied. Untouched rows keep the
 * viewer's loaded units, so a claim built from this list is the full set;
 * takeUnits clamps a change to what others left.
 */
export function shownClaimRows(
  items: ExpenseLineItem[] | undefined,
  pending: PendingClaims,
  viewerId: string,
  participants: readonly string[]
): ExpenseLineItem[] {
  if (!items) return [];
  if (!participants.includes(viewerId)) return items;
  return Object.entries(pending).reduce(
    (acc, [id, change]) =>
      takeUnits(acc, id, viewerId, change.want, participants),
    items
  );
}

/** Whether `shown` holds the viewer to different units than the loaded `items`. */
export function isClaimDirty(
  items: readonly ExpenseLineItem[] | undefined,
  shown: readonly ExpenseLineItem[],
  viewerId: string,
  participants: readonly string[]
): boolean {
  if (!items) return false;
  return shown.some(
    (row, idx) =>
      unitsHeld(row, viewerId, participants) !==
      unitsHeld(items[idx], viewerId, participants)
  );
}

/** Records the viewer's units on `id` in `next` as a change against the loaded row; equal units drop the change. */
export function settleClaim(
  prev: PendingClaims,
  id: string,
  items: readonly ExpenseLineItem[],
  next: readonly ExpenseLineItem[],
  viewerId: string,
  participants: readonly string[]
): PendingClaims {
  const loaded = items.find(row => row.id === id);
  const row = next.find(r => r.id === id);
  if (!loaded || !row) return prev;
  const want = unitsHeld(row, viewerId, participants);
  const base = unitsHeld(loaded, viewerId, participants);
  const out = { ...prev };
  if (want === base) delete out[id];
  else out[id] = { want, base };
  return out;
}

/**
 * A tap on a row's tick: takes one unit or releases them. Undoing a pending
 * release settles against the loaded rows, which restores the viewer's loaded
 * units that the normalised shown row may no longer be able to express.
 */
export function toggleClaim(
  prev: PendingClaims,
  id: string,
  items: ExpenseLineItem[],
  viewerId: string,
  participants: readonly string[]
): PendingClaims {
  const target =
    prev[id]?.want === 0
      ? items
      : toggleUnit(
          shownClaimRows(items, prev, viewerId, participants),
          id,
          viewerId,
          participants
        );
  return settleClaim(prev, id, items, target, viewerId, participants);
}

/** The stepper on a row; stepping back to the loaded units settles against the loaded row, as `toggleClaim` does. */
export function setClaimUnits(
  prev: PendingClaims,
  id: string,
  units: number,
  items: ExpenseLineItem[],
  viewerId: string,
  participants: readonly string[]
): PendingClaims {
  const target =
    prev[id] && units === prev[id].base
      ? items
      : takeUnits(
          shownClaimRows(items, prev, viewerId, participants),
          id,
          viewerId,
          units,
          participants
        );
  return settleClaim(prev, id, items, target, viewerId, participants);
}

/**
 * Each row as `viewerId` reads it, names under "Taken by" in `order`, and the
 * one row that carries the shared-by-everyone rule (the first untouched row,
 * once any row is touched). With `pending`, a row the viewer is releasing
 * stays tappable even when the release reads as fully taken.
 */
export function viewerAvailabilities(
  rows: readonly ExpenseLineItem[],
  viewerId: string,
  participants: readonly string[],
  order: readonly string[],
  pending?: PendingClaims
): { availabilities: Map<string, ItemAvailability>; hintedId?: string } {
  const ranks = new Map(order.map((id, idx) => [id, idx]));
  const rank = (id: string) => ranks.get(id) ?? Number.MAX_SAFE_INTEGER;
  const availabilities = new Map<string, ItemAvailability>();
  for (const row of rows) {
    const raw = availabilityOf(row, viewerId, participants);
    const change = pending?.[row.id];
    // A legacy row with as many sharers as units reads as fully taken once
    // the viewer releases it, so without this it would stop being tappable.
    const read: ItemAvailability =
      raw.kind === 'taken' && change && change.want < change.base
        ? {
            kind: 'partial',
            quantity: raw.quantity,
            left: change.base - change.want,
            takenBy: raw.takenBy,
          }
        : raw;
    availabilities.set(
      row.id,
      read.kind === 'partial' || read.kind === 'taken'
        ? {
            ...read,
            takenBy: [...read.takenBy].sort((x, y) => rank(x) - rank(y)),
          }
        : read
    );
  }
  const hintedId = rows.some(row => availabilities.get(row.id)?.kind !== 'free')
    ? rows.find(row => availabilities.get(row.id)?.kind === 'free')?.id
    : undefined;
  return { availabilities, hintedId };
}

/** What the preview reads, so the link page's bill (no `originalAmount`) fits too. */
export type ItemsPreviewExpense = Pick<
  ExpenseResponseDto,
  | 'amount'
  | 'currency'
  | 'lineItemCurrency'
  | 'lineItemsRest'
  | 'lineItemParticipants'
  | 'unpickedSplit'
> &
  Partial<Pick<ExpenseResponseDto, 'originalAmount'>>;

/** The expense's split with `rows` in place of its stored items. */
export function expenseItemsPreview(
  expense: ItemsPreviewExpense,
  rows: ExpenseLineItem[]
): {
  preview: LineItemSplitResult | null;
  entryCurrency: string;
  foreign: boolean;
  rest: LineItemsRest;
} {
  const entryCurrency = expense.lineItemCurrency ?? expense.currency;
  const foreign =
    entryCurrency !== expense.currency && expense.originalAmount != null;
  const rest = expense.lineItemsRest ?? DEFAULT_REST;
  const preview = previewLineItemSplit(
    {
      itemsMode: true,
      lineItems: rows,
      rest,
      participants: expense.lineItemParticipants ?? [],
      fromScan: false,
      scanReconciled: false,
      scanUnreconciled: false,
    },
    expense.amount,
    expense.currency,
    foreign
      ? { amount: expense.originalAmount as number, currency: entryCurrency }
      : undefined,
    expense.unpickedSplit
  );
  return { preview, entryCurrency, foreign, rest };
}
